/* lib/analyticsAccess.js — реестр доступов команды: аналитика iiko/Syrve + «Продукция».
 * ЗЕРКАЛО backend/integrations/access_catalog.py: ключи, русские лейблы и умолчания
 * совпадают байт-в-байт (конвенция inventoryCriteria/abcMeta/foodcostMeta; сторож —
 * analyticsAccess.test.js читает файл бэкенда).
 *
 * Ключ: `analytics.<tab>` (вкладка) и `analytics.<tab>.<section>` (секция) — умолчание
 * «всё открыто»; `kitchen.<key>` (права «Продукции», pos-base-rework) — умолчание у КАЖДОГО
 * ключа своё: правка открыта, публикация в кассу закрыта (владелец и администраторы — всегда
 * могут, это решает бэкенд). Карта эффективных прав текущего юзера приходит в
 * GET /api/integrations/ полем `access` ({key: bool}); отсутствие карты или ключа = умолчание
 * каталога, `false` — строго закрыто. Запрет вкладки закрывает все её секции. */

export const ACCESS_PREFIX = 'analytics';

// Вкладки-отчёты (порядок = порядок вкладок в UI).
export const ACCESS_TABS = [
  { key: 'overview', label: 'Обзор' },
  { key: 'pnl', label: 'ОПиУ' },
  { key: 'abc', label: 'ABC-анализ блюд' },
  { key: 'liquidity', label: 'Ликвидность' },
  { key: 'categories', label: 'Продажи' },
  { key: 'purchases', label: 'Закупки' },
  { key: 'movement', label: 'Движение товара' },
  { key: 'inventory', label: 'Инвентаризация' },
  // «Персонал» (бывш. «Официанты», staff-groups): вкладка делится по ролям из кассы.
  // Ключ `staff` и права прежние — переименована только подпись.
  { key: 'staff', label: 'Персонал' },
  { key: 'settings', label: 'Настройки' },
];

// Секции внутри вкладок (для матрицы ролей и гейтов рендера).
export const ACCESS_SECTIONS = {
  overview: [
    { key: 'today', label: 'Сегодня и вчера' },
    { key: 'plan', label: 'План выручки' },
    { key: 'income', label: 'Доходы' },
    { key: 'dynamics', label: 'Динамика выручки' },
    { key: 'guests', label: 'Гости, средний чек, заказы' },
    { key: 'performance', label: 'Производительность' },
    { key: 'tables', label: 'Столы: спрос и загрузка' },
    { key: 'ai_review', label: 'AI-разбор' },
  ],
  pnl: [
    { key: 'summary', label: 'Сводка месяца' },
    { key: 'tree', label: 'Таблица ОПиУ' },
    { key: 'expenses_edit', label: 'Ручные расходы (правка)' },
    { key: 'ai_review', label: 'AI-разбор' },
  ],
  abc: [
    { key: 'manage', label: 'Пороги и исключения (правка)' },
    { key: 'ai_review', label: 'AI-разбор' },
  ],
  liquidity: [
    { key: 'window_edit', label: 'Окно расхода (правка)' },
    { key: 'ai_review', label: 'AI-разбор' },
  ],
  categories: [
    { key: 'charts', label: 'Графики (структура, динамика, рост)' },
    { key: 'table', label: 'Таблица и карточка разреза' },
    { key: 'ai_review', label: 'AI-разбор' },
  ],
  purchases: [
    { key: 'charts', label: 'Графики (структура, динамика)' },
    { key: 'suppliers', label: 'Поставщики' },
    { key: 'products', label: 'Топ товаров' },
    { key: 'ai_review', label: 'AI-разбор' },
  ],
  movement: [
    { key: 'ai_review', label: 'AI-разбор' },
  ],
  inventory: [
    { key: 'ai_review', label: 'AI-разбор' },
  ],
  staff: [
    { key: 'leakage', label: 'Потери и удаления (скидки, возвраты, категории удалений, причины)' },
    { key: 'waiter_detail', label: 'Карточка официанта' },
    { key: 'manage', label: 'Скрытые сотрудники (правка)' },
    { key: 'ai_review', label: 'AI-разбор' },
  ],
  settings: [],
};

export function accessKey(tab, section) {
  return ACCESS_PREFIX + '.' + tab + (section ? '.' + section : '');
}

// Доступна ли вкладка. access — карта {key: bool} из GET /api/integrations/
// (может отсутствовать: personal ws / старый бэк → всё разрешено).
export function canTab(access, tab) {
  if (!access) return true;
  return access[accessKey(tab)] !== false;
}

// Доступна ли секция: закрытая вкладка закрывает и все её секции.
export function canSection(access, tab, section) {
  if (!access) return true;
  if (!canTab(access, tab)) return false;
  return access[accessKey(tab, section)] !== false;
}

// ── «Продукция» (pos-base-rework): два права с разными умолчаниями ──────────
// Порядок = порядок тумблеров в редакторе ролей. `default` — что получает участник
// без явного значения (роль и личное исключение могут выставить любой bool).
export const KITCHEN_PREFIX = 'kitchen';

export const KITCHEN_ACCESS = [
  { key: 'edit', label: 'Продукция: правка (ТТК, сырьё, меню)', default: true },
  { key: 'publish', label: 'Продукция: публикация в кассу', default: false },
  // Сеть заведений (iiko Chain): отправка меняет рецепт во всех точках — отдельное право.
  { key: 'publish_chain', label: 'Продукция: публикация в сеть заведений', default: false },
];

export function kitchenKey(key) {
  return KITCHEN_PREFIX + '.' + key;
}

// Умолчание каталога для короткого ключа «Продукции»; неизвестный ключ — открыт
// (общее правило «отсутствие = разрешено», как у Аналитики).
export function kitchenDefault(key) {
  const item = KITCHEN_ACCESS.find((k) => k.key === key);
  return item ? item.default !== false : true;
}

// ── «Аргус» (№523): пользоваться ИИ-ассистентом, по умолчанию открыто ─────────
// Зеркало `ASSISTANT_KEYS` бэкенда (integrations/access_catalog.py, тест-зеркало сверяет).
// Лимит запросов — не ключ, а число у роли/участника (`assistant_limit`).
export const ASSISTANT_PREFIX = 'assistant';

export const ASSISTANT_ACCESS = [
  { key: 'use', label: 'Аргус: пользоваться', default: true },
];

export function assistantKey(key) {
  return ASSISTANT_PREFIX + '.' + key;
}

// Умолчание по ПОЛНОМУ ключу карты access (для подписей «Наследовать»/tri-state):
// `kitchen.<key>` — из каталога «Продукции», `assistant.<key>` — из «Аргуса»,
// всё остальное (analytics.*) — открыто.
export function accessDefault(fullKey) {
  const prefix = KITCHEN_PREFIX + '.';
  if (typeof fullKey === 'string' && fullKey.startsWith(prefix)) {
    return kitchenDefault(fullKey.slice(prefix.length));
  }
  const aPrefix = ASSISTANT_PREFIX + '.';
  if (typeof fullKey === 'string' && fullKey.startsWith(aPrefix)) {
    const item = ASSISTANT_ACCESS.find((k) => k.key === fullKey.slice(aPrefix.length));
    return item ? item.default !== false : true;
  }
  return true;
}

// Эффективное право «Продукции» по карте access из GET /api/integrations/:
// нет карты или ключа → умолчание каталога; явный bool побеждает; не-bool = как отсутствие.
// Ключ вне каталога — ЗАКРЫТО (как `kitchen_allowed` на бэкенде): опечатка у потребителя не
// должна показывать кнопку, которую сервер отклонит 403.
export function canKitchen(access, key) {
  if (!KITCHEN_ACCESS.some((k) => k.key === key)) return false;
  const def = kitchenDefault(key);
  if (!access) return def;
  const v = access[kitchenKey(key)];
  if (v === false) return false;
  if (v === true) return true;
  return def;
}

// Полный список ключей каталога (для матриц ролей/исключений): вкладки, секции, «Продукция».
export function allAccessKeys() {
  const keys = ACCESS_TABS.map((t) => accessKey(t.key));
  for (const t of ACCESS_TABS) {
    for (const s of (ACCESS_SECTIONS[t.key] || [])) keys.push(accessKey(t.key, s.key));
  }
  for (const k of KITCHEN_ACCESS) keys.push(kitchenKey(k.key));
  return keys;
}
